import React from 'react';
import { useState } from 'react';
import { useAppSelector, useTranslate } from '../../../hooks/common';
import Accordion from '../../common/Accordion';
import { TRANSLATIONS } from '../../../constants';
import { currentRoundToString, getPowerUnit, getTorqueUnit, isNullOrUndefined, percentageRound, powerRoundToString, round, speedRoundToString, startupsRound, timeRound, torqueRoundToString } from '../../../services';
import { StartingType } from '../../../enums';
import { AccelerationReserve, StartupNumbers } from '../../../models';
import DataRow from './DataRow';

const LoadOperationDataLine = () => {
    const translate = useTranslate();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const [accordionOpen, toggleAccordion] = useState(true);

    const getStartupNumbersData = ({ warm, cold }: StartupNumbers) => {
        const roundedWarm = startupsRound(warm);
        const roundedCold = startupsRound(cold);

        return roundedWarm === 0 && roundedCold === 0
            ? translate(TRANSLATIONS.result.loadOperationData.noStartUp)
            : `${roundedCold}x ${translate(TRANSLATIONS.result.loadOperationData.cold)} / ${roundedWarm}x ${translate(TRANSLATIONS.result.loadOperationData.warm)}`;
    };
    
    const getAccelerationReserveData = ({ torque, percentage }: AccelerationReserve, unit: string) =>
        `${torqueRoundToString(torque)} ${unit} ${percentageRound(percentage * 100)}%`;

    const renderDataTable = () => {
        if (calculationResponse) {
            const { unitSystem, commonData, currentData, motorData } = mesRequest;
            const { startingType, startingVoltage, loadData } = calculationResponse.mesParameters.inputData;
            const { startupNumbers, startupTime, startupTimeDelta, startupTimeStar, accelerationTorqueAverage, accelerationReserve, powerReserve,
                loadPower, loadSpeed, loadCurrent, motorVoltage, frequency, loadTorque } = calculationResponse.loadOperationValues;
            const isStartingTypeYD = startingType === StartingType.YD;
            const isSpecificVoltageCase = !isStartingTypeYD && startingVoltage < 1;
            const torqueUnit = getTorqueUnit(unitSystem);
            const powerUnit = getPowerUnit(unitSystem);

            return (
                <div className='data-table'>
                    <DataRow
                        value={translate(TRANSLATIONS.result.loadOperationData.voltage)}
                        specificValue={isSpecificVoltageCase ? translate(TRANSLATIONS.result.loadOperationData.specificVoltage, round(startingVoltage * 100, 0)) : undefined}
                    />
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.numberOfStartUps)}
                        value={startupNumbers && getStartupNumbersData(startupNumbers.base)}
                        specificValue={startupNumbers && startupNumbers.specific && getStartupNumbersData(startupNumbers.specific)}
                        isSpecificVoltageCase={isSpecificVoltageCase}
                    />
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.startUpTime)}
                        value={startupTime && `${timeRound(startupTime.base)} [s]`}
                        specificValue={startupTime && !isNullOrUndefined(startupTime.specific) ? `${timeRound(startupTime.specific)} [s]` : undefined}
                        isSpecificVoltageCase={isSpecificVoltageCase}
                    />
                    {isStartingTypeYD && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.startUpTimeDelta)}
                        value={startupTimeDelta && `${timeRound(startupTimeDelta)} [s]`}
                    />}
                    {isStartingTypeYD && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.startUpTimeStar)}
                        value={startupTimeStar && `${timeRound(startupTimeStar)} [s]`}             
                    />}
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.accelerationTorqueAverage)}
                        value={accelerationTorqueAverage && `${torqueRoundToString(accelerationTorqueAverage.base)} ${torqueUnit}`}
                        specificValue={accelerationTorqueAverage && !isNullOrUndefined(accelerationTorqueAverage.specific) ? `${torqueRoundToString(accelerationTorqueAverage.specific)} ${torqueUnit}` : undefined}
                        isSpecificVoltageCase={isSpecificVoltageCase}
                    />
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.accelerationReserve)}
                        value={accelerationReserve && getAccelerationReserveData(accelerationReserve.base, torqueUnit)}
                        specificValue={accelerationReserve && accelerationReserve.specific && getAccelerationReserveData(accelerationReserve.specific, torqueUnit)}
                        isSpecificVoltageCase={isSpecificVoltageCase}
                    />
                    {motorData && !isNullOrUndefined(motorData.power) && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.ratedMotorPower)}
                        value={`${powerRoundToString(motorData.power ?? 0)} ${powerUnit}`}
                    />}
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.loadPower)}
                        value={loadPower && `${powerRoundToString(loadPower.base)} ${powerUnit}`}
                    />
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.powerReserve)}
                        value={!isNullOrUndefined(powerReserve) ? `${percentageRound(powerReserve * 100)}%` : ''}
                    />
                    {commonData && !isNullOrUndefined(commonData.mn) && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.ratedMotorTorque)}
                        value={`${torqueRoundToString(commonData.mn ?? 0)} ${torqueUnit}`}
                    />}
                    {loadData && loadData.m4 && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.loadOperatingTorque)}
                        value={`${torqueRoundToString(loadData.m4)} ${torqueUnit}`}
                    />}
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.loadTorque)}
                        value={loadTorque && `${torqueRoundToString(loadTorque.base)} ${torqueUnit}`}
                    />
                    {commonData && commonData.nN && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.ratedMotorSpeed)}
                        value={`${speedRoundToString(commonData.nN)} ${translate(TRANSLATIONS.units.rpm)}`}
                    />}
                    {loadData && loadData.n4 && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.loadOperatingSpeed)}
                        value={`${speedRoundToString(loadData.n4)} ${translate(TRANSLATIONS.units.rpm)}`}
                    />}
                    <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.loadSpeed)}
                        value={loadSpeed && `${speedRoundToString(loadSpeed.base)} ${translate(TRANSLATIONS.units.rpm)}`}
                    />
                    {currentData && !isNullOrUndefined(currentData.ratedCurrent) && <DataRow
                        label={translate(TRANSLATIONS.result.loadOperationData.ratedMotorCurrent)}
                        value={`${currentRoundToString(currentData.ratedCurrent ?? 0)}A @ ${motorVoltage.base}V ${frequency}Hz`}
                    />}
                    {[loadCurrent, motorVoltage, frequency].every(x => !isNullOrUndefined(x)) && !isNullOrUndefined(loadCurrent.base) &&
                        <DataRow
                            label={translate(TRANSLATIONS.result.loadOperationData.loadCurrent)}
                            value={`${currentRoundToString(loadCurrent.base)}A @ ${motorVoltage.base}V ${frequency}Hz`}
                        />
                    }
                </div>
            );
        }
    };

    return (
        <div className='load-operation-data-line-container'>
            <Accordion
                label={translate(TRANSLATIONS.result.loadOperationData.title)}
                iconFont={'e90c'}
                open={accordionOpen}
                onClick={() => toggleAccordion(!accordionOpen)}
            >
                {renderDataTable()}
            </Accordion>
        </div>
    );
};

export default LoadOperationDataLine;
